import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type { IntlService } from 'ember-intl';
import type { Carate, RoomCategoryFilter } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS,
  type OpenPricingRoomCategoryToRIRoomTypes,
  type OpenRoomPricingFixedCategory,
  PRIMA_VIEWS,
  RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING,
} from 'lumen/utils/constants/pricing-assistant-constants';

/**
 * Find the carate with the lowest average price priority
 * If an empty array is passed, it will return null
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function findCarateWithLowestAveragePricePriority(carates: Carate[]) {
  if (carates.length === 0) {
    return null;
  }

  return carates.reduce((lowestPriorityCarate, carate) =>
    carate.averagePricePriority < lowestPriorityCarate.averagePricePriority
      ? carate
      : lowestPriorityCarate,
  );
}

/**
 * Check if the carate matches a room category
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isCarateForCategory(
  carate: Carate,
  roomCategory: OpenRoomPricingFixedCategory,
) {
  return carate.cemaCategory === roomCategory;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isCarateForSelectedGuest(
  carate: Carate,
  selectedGuest: string,
) {
  // We don't do any carate filtering if the carate is sold out or if the selected guest is "Any"
  return (
    carate.isSoldOut ||
    carate.maxPersons.toString() === selectedGuest.toString()
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isCarateForRateCode(carate: Carate, rateCode: string) {
  return carate.rateCode === rateCode;
}

export function selectedRoomCategory(
  roomCategory: OpenPricingRoomCategoryToRIRoomTypes,
): OpenRoomPricingFixedCategory {
  const mapping = RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING;

  return roomCategory in mapping
    ? (mapping[roomCategory] as OpenRoomPricingFixedCategory)
    : roomCategory;
}

export function translateRoomCategory(intl: IntlService, room: string): string {
  return intl.t(
    OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS[
      room as keyof typeof OPEN_ROOM_PRICING_ROOM_CATEGORIES_TRANSLATIONS
    ],
  );
}

/**
 * Validate open room pricing dynamic roomType and maxPersons params in the calendar view
 */

export function validateOpenPricingCalendarParams(
  queryParams: Record<string, unknown> | undefined,
  store: Store,
  hotelAndMarket: HotelAndMarketServiceWithHotel,
  router: RouterService,
): void {
  const roomCategoryFiltersRequestTaskResult = store
    .peekAll<RoomCategoryFilter>('room-category-filter')
    .slice();
  const params = queryParams;

  if (!params) {
    return;
  }

  if (
    params['view'] === PRIMA_VIEWS.CALENDAR &&
    params['roomType'] &&
    params['maxPersons'] &&
    roomCategoryFiltersRequestTaskResult
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const roomCategory =
      // @ts-expect-error: Type '{}' cannot be used as an index type.
      RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING[params['roomType']];
    const filterForRoomCategory = roomCategoryFiltersRequestTaskResult.find(
      (filter: RoomCategoryFilter) => filter.roomCategory === roomCategory,
    );

    const isValidMaxPersons = Boolean(
      filterForRoomCategory?.occupancy?.includes(
        // @ts-expect-error: Argument of type '{}' is not assignable to parameter of type 'string'.
        parseInt(params['maxPersons'], 10),
      ),
    );

    let newQueryParams = {};

    // Case 1: Invalid roomType
    if (!filterForRoomCategory) {
      // Select base room category
      const validRoomType = hotelAndMarket.hotel.paRatePreference.room_type.id;
      const validMaxPersons = roomCategoryFiltersRequestTaskResult
        .find(
          (filter: RoomCategoryFilter) =>
            filter.roomCategory ===
            RI_ROOMTYPE_TO_OPEN_PRICING_ROOM_CATEGORY_MAPPING[validRoomType],
        )
        ?.occupancy?.sort((a: number, b: number) => a - b)?.[0]
        ?.toString();

      newQueryParams = {
        roomType: validRoomType,
        maxPersons: validMaxPersons,
      };
    } else if (!isValidMaxPersons) {
      // Case 2: Valid roomType but invalid maxPersons
      const validMaxPersons = filterForRoomCategory.occupancy
        ?.sort((a: number, b: number) => a - b)?.[0]
        ?.toString();
      newQueryParams = {
        maxPersons: validMaxPersons,
      };
    }

    if (Object.keys(newQueryParams).length) {
      router.replaceWith(router.currentRouteName, {
        queryParams: newQueryParams,
      });
    }
  }
}
